import { useState, useEffect } from 'react';
import useSWR from 'swr';
import Cookies from 'js-cookie';

import { IRepository } from '@/types';
import { IResponseError } from '@/lib/apiBase';
import { fetchVersionsForFile, IFileVersion, IRepositoryBranchVersionsResponse } from '@/lib/tabularFileApi';
import { IRepositoryBranch } from '@/lib/repositoryBranchesApi';

interface useFileVersionsParams {
  repository: IRepository;
  branch?: IRepositoryBranch;
  filePath: string;
  initialData?: IRepositoryBranchVersionsResponse | undefined;
}

const prependHeadVersion = (versions: IFileVersion[], path: string, branch: IRepositoryBranch): IFileVersion[] => {
  const sortedVersions = [...versions].sort((a, b) => (!!a.commit.timestamp > !!b.commit.timestamp ? -1 : 1));
  const headVersion: IFileVersion = {
    resource: { path: path, version: branch?.name as string },
    commit: {
      id: 'HEAD',
      message: 'HEAD - Track head of the selected branch',
      timestamp: 'EVERGREEN',
      author: '',
      email: '',
    },
    schema_hash: sortedVersions[0]?.schema_hash,
  };
  return [headVersion].concat(versions);
};

const useFileVersions = ({
  repository,
  branch,
  filePath,
  initialData, // onProgress,
}: useFileVersionsParams) => {
  // console.log('useFileVersions', { repository, branch, filePath, initialData });
  const token = Cookies.get('oxen_user_token');
  const branchName = branch?.name || repository.default_branch || 'main';

  const [status, setStatus] = useState<'success' | 'error' | 'pending'>('pending');
  const [versions, setVersions] = useState<IFileVersion[]>(initialData?.versions || []);
  const [error, setError] = useState<IResponseError | undefined>();

  const { data, isLoading, isValidating } = useSWR(
    [repository.namespace, repository.name, branchName, filePath],
    () =>
      fetchVersionsForFile({
        namespace: repository.namespace,
        repositoryName: repository.name,
        branchName: branchName,
        filePath: filePath,
        token: token,
      }),
    {
      fallbackData: initialData,
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateOnMount: !!filePath,
      revalidateOnReconnect: false,
    },
  );

  useEffect(() => {
    if (data && data.versions) {
      setVersions(prependHeadVersion(data.versions, filePath, branch as IRepositoryBranch));
      setStatus(data?.status);
      setError(data?.error as IResponseError);
    }
  }, [data, branch, filePath]);

  return {
    status: status,
    isLoading: isLoading,
    isValidating: isValidating,
    versions: versions,
    error: error,
  };
};

export default useFileVersions;
